import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import { useMemo, useState } from "react";
import * as yup from "yup";
import { I18n } from "react-redux-i18n";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import FormButton from "./FormButton";
import { Auth } from "aws-amplify";
import { normalizeUser } from "../../utils/normalizeUser";
import { connect } from "react-redux";
import { setUserProfile } from "../../state/actions/user";

const ResetPassword = ({ initialValue, setUserProfile }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [resendLoader, setResendLoader] = useState(false);
  const [error, setError] = useState(false);
  const theme = useTheme();

  const schema = useMemo(() => {
    return yup.object({
      code: yup.string().required(I18n.t("authenticator.required")),
      password: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .min(6, I18n.t("authenticator.minLength")),
      confirmPassword: yup
        .string()
        .required(I18n.t("authenticator.passRepeatRequired"))
        .test(
          "passwords-match",
          I18n.t("authenticator.passwordsNotMatch"),
          function (value) {
            return this.parent.password === value;
          }
        ),
    });
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      code: "",
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(schema),
  });

  const codeErrorMsg = useMemo(() => {
    if (error) return I18n.t("authenticator.CodeMismatchException");

    return errors?.code?.message || "";
  }, [errors?.code?.message, error]);

  const onResend = async () => {
    setResendLoader(true);

    try {
      await Auth.forgotPassword(initialValue.email);
    } catch (e) {
      console.log(e);
    }

    setResendLoader(false);
  };

  const onSubmit = async (data) => {
    setError(false);
    setLoader(true);

    try {
      await Auth.forgotPasswordSubmit(
        initialValue.email,
        data.code,
        data.password
      );

      const user = await Auth.signIn(initialValue.email, data.password);

      setUserProfile(normalizeUser(user));

      return;
    } catch (e) {
      console.log(e);

      if (e.code === "CodeMismatchException") setError(true);
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
            marginBottom: "4px",
          }}
        >
          {I18n.t("authenticator.resetPassword")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="code"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.verificationCode")}
              variant="outlined"
              {...field}
              error={(isSubmitted && Boolean(errors.code)) || error}
              helperText={codeErrorMsg}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.password")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.password)}
              helperText={errors?.password?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.repeatPass")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={isSubmitted && Boolean(errors.confirmPassword)}
              helperText={errors?.confirmPassword?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography style={{ color: theme.palette.auth.readonly }}>
          {I18n.t("authenticator.verificationText")}
        </Typography>
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton
          {...{
            label: I18n.t("authenticator.done"),
            loader,
            disabled: resendLoader,
          }}
        />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {resendLoader ? (
          <CircularProgress size="20px" />
        ) : (
          <Button color="primary" disabled={loader} onClick={onResend}>
            {I18n.t("authenticator.resendCode")}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setUserProfile: (user) => dispatch(setUserProfile(user)),
});

export default connect(null, mapDispatchToProps)(ResetPassword);
